import { useMemo } from 'react'

import { getCurrency } from '@common/utils/currency.ts'
import type { ColumnDef } from '@tanstack/react-table'

import {
  DataTable,
  PaginationTypes,
} from '@/components/ui/table/controlled-pagination/DataTable.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import type {
  SpendingCustomerListContent,
  SpendingCustomerListResponse,
} from '@/modules/tracking/new-subscriber-analysis/types.ts'
import { ViewCustomerListAction } from '@/modules/tracking/new-subscriber-analysis/ui/components/view-customer-list-action.tsx'
import { CustomerListTableSkeleton } from '@/modules/tracking/new-subscriber-analysis/ui/table/customer-list-table-skeleton.tsx'
import { usePagination } from '@/utils/pagination/pagination-context.tsx'

function buildColumns(): ColumnDef<SpendingCustomerListContent>[] {
  return [
    {
      header: 'Name',
      accessorKey: 'displayName',
      enableSorting: false,
      cell: ({ getValue, row }) => (
        <div className="flex items-center gap-2.5">
          <CreatorAvatar
            avatarSrc={row.original.avatar}
            name={row.original.displayName}
            size={40}
          />
          <div className="font-bold">{getValue() as string}</div>
        </div>
      ),
    },
    {
      header: 'Spend',
      accessorKey: 'totalRevenue',
      enableSorting: false,
      cell: ({ getValue }) => <div>{getCurrency(getValue() as string, 0)}</div>,
    },
    {
      header: () => <span className="w-full text-right">Actions</span>,
      id: 'action',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          <Tooltip content="View in Chat">
            <ViewCustomerListAction row={row.original} />
          </Tooltip>
        </div>
      ),
    },
  ]
}

interface Props {
  spendingList: SpendingCustomerListResponse | null
  isFetching: boolean
}

export const CustomerListTable = ({ spendingList, isFetching }: Props) => {
  const { pagination, setPagination } = usePagination()

  const columns = useMemo(() => buildColumns(), [])

  if (isFetching) {
    return <CustomerListTableSkeleton />
  }

  return (
    <DataTable
      columns={columns}
      data={spendingList?.content || []}
      paginationVariant={PaginationTypes.DEFAULT}
      enablePagination
      stickyHeaderSize="400px"
      pageCount={spendingList?.totalPages || 0}
      paginationState={pagination}
      onPaginationChange={setPagination}
      tbodyMinHeight={false}
    />
  )
}
